import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useDataManager } from '../hooks/useDataManager';

const SiteContext = createContext();

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    return {
      currentSite: null,
      sites: [],
      setCurrentSite: () => {},
      refreshSites: () => {},
      createSite: () => {},
      updateSite: () => {},
      deleteSite: () => {},
      loading: false,
      error: null,
    };
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const {
    getDataWithConfidence,
    saveUnifiedData,
    checkUnifiedDataStatus,
    dataStates,
    loading: dataLoading,
    error: dataError
  } = useDataManager();
  
  const [currentSite, setCurrentSiteState] = useState(null);

  // Get sites from useDataManager
  const sitesData = getDataWithConfidence('sites');
  const sites = sitesData?.formData || [];
  const loading = dataLoading.sites || false;
  const error = dataError.sites || null;

  // Load sites when user authenticates
  useEffect(() => {
    if (isAuthenticated) {
      loadUserSites();
    } else {
      // Clear sites when user logs out
      setCurrentSiteState(null);
    }
  }, [isAuthenticated]);

  const loadUserSites = async () => {
    try {
      // Use useDataManager to check for site data
      const result = await checkUnifiedDataStatus('sites');
      
      if (result.exists && result.data) {
        const userSites = Array.isArray(result.data) ? result.data : [result.data];
        
        // Set current site from stored preference or first site
        const storedCurrentSite = localStorage.getItem(`current_site_${user?.id}`);
        if (storedCurrentSite && userSites.find(site => site.id === storedCurrentSite)) {
          setCurrentSiteState(storedCurrentSite);
        } else if (userSites.length > 0) {
          setCurrentSiteState(userSites[0].id);
        }
      }
      
    } catch (err) {
      console.error('Failed to load user sites:', err);
    }
  };
  const gatherAllData = () => {
    const data = {
      hasData: false,
      siteInfo: null,
      analyses: [],
    };
    
    return data;
  };
  
  const setCurrentSite = (siteId) => {
    setCurrentSiteState(siteId);
    // Store preference
    if (user?.id) {
      localStorage.setItem(`current_site_${user.id}`, siteId);
    }
  };

  const refreshSites = async () => {
    await loadUserSites();
  };

  const createSite = async (siteData) => {
    try {
      const newSite = {
        id: `site_${user?.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...siteData,
        userId: user?.id,
        createdAt: new Date().toISOString(),
        analyses: [],
        businessCase: null,
        implementation: null,
        stats: {
          totalAnalyses: 0,
          completedTasks: 0,
          lastActivity: Date.now()
        }
      };

      const updatedSites = [...sites, newSite];
      
      // Save via useDataManager
      await saveUnifiedData('sites', updatedSites, 'user-entered');
      
      // Set as current site
      setCurrentSite(newSite.id);
      
      return newSite;
    } catch (err) {
      console.error('Failed to create site:', err);
      throw err;
    }
  };

  const updateSite = async (siteId, updates) => {
    try {
      const updatedSites = sites.map(site => 
        site.id === siteId 
          ? { ...site, ...updates, updatedAt: new Date().toISOString() }
          : site
      );
      
      // Save via useDataManager
      await saveUnifiedData('sites', updatedSites, 'user-entered');
      
      return updatedSites.find(site => site.id === siteId);
    } catch (err) {
      console.error('Failed to update site:', err);
      throw err;
    }
  };

  const deleteSite = async (siteId) => {
    try {
      const updatedSites = sites.filter(site => site.id !== siteId);
      
      // Save via useDataManager
      await saveUnifiedData('sites', updatedSites, 'user-entered');
      
      // If deleting current site, switch to another or null
      if (currentSite === siteId) {
        const newCurrentSite = updatedSites.length > 0 ? updatedSites[0].id : null;
        setCurrentSite(newCurrentSite);
      }
      
    } catch (err) {
      console.error('Failed to delete site:', err);
      throw err;
    }
  };

  const addAnalysisToSite = async (siteId, analysisData) => {
    const updatedSites = sites.map(site => {
      if (site.id === siteId) {
        const newAnalysis = {
          ...analysisData,
          id: analysisData.id || `analysis_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          siteId: siteId,
          createdAt: analysisData.createdAt || new Date().toISOString()
        };
        
        return {
          ...site,
          analyses: [...site.analyses, newAnalysis],
          stats: {
            ...site.stats,
            totalAnalyses: site.analyses.length + 1,
            lastActivity: Date.now()
          }
        };
      }
      return site;
    });
    
    // Save via useDataManager
    await saveUnifiedData('sites', updatedSites, 'user-entered');
  };

  const getCurrentSiteData = () => {
    return sites.find(site => site.id === currentSite) || null;
  };

  const contextValue = {
    currentSite,
    sites,
    setCurrentSite,
    refreshSites,
    createSite,
    updateSite,
    deleteSite,
    addAnalysisToSite,
    getCurrentSiteData,
    loading,
    error,
  };

  return (
    <SiteContext.Provider value={contextValue}>
      {children}
    </SiteContext.Provider>
  );
};

export default SiteContext;
